import React from 'react';
import { motion } from 'framer-motion';
import { ShoppingBagIcon } from 'lucide-react';
import { CHECKOUT_URL, trackCheckout } from '../utils/checkout';
type ButtonVariant = 'primary' | 'inverse' | 'secondary';
type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  children: React.ReactNode;
  className?: string;
  href?: string;
  target?: string;
  rel?: string;
  onLinkClick?: () => void;
  showIcon?: boolean;
  variant?: ButtonVariant;
};
const variantClasses: Record<ButtonVariant, string> = {
  primary: 'border-accent bg-primary-strong text-primary-foreground shadow-elevated hover:bg-primary focus-visible:ring-primary focus-visible:ring-offset-background',
  inverse: 'border-accent bg-background text-primary-strong shadow-[0_10px_28px_rgb(0_0_0/0.28)] hover:bg-surface hover:border-accent-soft focus-visible:ring-accent-soft focus-visible:ring-offset-inverse',
  secondary: 'border-primary bg-surface text-primary-strong hover:bg-surface-subtle focus-visible:ring-primary focus-visible:ring-offset-background'
};
const iconClasses: Record<ButtonVariant, string> = {
  primary: 'text-accent-soft',
  inverse: 'text-primary',
  secondary: 'text-primary'
};
export function Button({
  children,
  className = '',
  href,
  target,
  rel,
  onLinkClick,
  showIcon = true,
  variant = 'primary',
  ...props
}: ButtonProps) {
  const baseClasses = `inline-flex items-center justify-center gap-2.5 rounded-xl border px-6 py-3.5 text-center text-sm font-bold uppercase tracking-[0.04em] transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 sm:px-8 sm:text-base ${variantClasses[variant]}`;
  const content = <>
      {showIcon && <ShoppingBagIcon size={18} strokeWidth={2} aria-hidden="true" className={`shrink-0 ${iconClasses[variant]}`} data-id="element-51" data-mp="3liblosbygedr" />}
      {children}
    </>;
  if (href) {
    return <motion.a href={href} target={target} rel={rel ?? (target === '_blank' ? 'noopener noreferrer' : undefined)} onClick={onLinkClick} className={`${baseClasses} ${className}`} whileHover={{
      y: -2
    }} whileTap={{
      scale: 0.98
    }} data-id="element-52" data-mp="1jp48ppjte8v1">
        {content}
      </motion.a>;
  }
  return <motion.button className={`${baseClasses} ${className}`} whileHover={{
    y: -2
  }} whileTap={{
    scale: 0.98
  }} {...props} data-id="element-53" data-mp="13cr71hwu2tjs">
      {content}
    </motion.button>;
}
export function CheckoutButton({
  children,
  className = '',
  variant = 'primary'
}: {
  children: React.ReactNode;
  className?: string;
  variant?: ButtonVariant;
}) {
  return <Button href={CHECKOUT_URL} target="_blank" onLinkClick={trackCheckout} className={className} variant={variant} data-id="element-54" data-mp="1m9sfjcpljbsh">
      {children}
    </Button>;
}